import React from 'react';

if (typeof document !== 'undefined' && !document.getElementById('sit-datepicker-css')) {
  const el = document.createElement('style');
  el.id = 'sit-datepicker-css';
  el.textContent = `
/* ---- Field trigger (shared with TextField styling) ---- */
.sit-dp { display: flex; flex-direction: column; gap: 6px; position: relative; }
.sit-dp__label { font-size: var(--text-sm); font-weight: var(--weight-semibold); color: var(--text-body); display: inline-flex; align-items: center; gap: 4px; }
.sit-dp__req { color: var(--red-500); }
.sit-dp__trigger {
  display: flex; align-items: center; gap: 10px; width: 100%; height: var(--control-h-md);
  padding: 0 12px; text-align: left; cursor: pointer;
  font-family: var(--font-body); font-size: var(--text-md); color: var(--text-strong);
  background: var(--slate-0); border: 1px solid var(--border-default); border-radius: var(--radius-md);
  transition: border-color var(--dur-base) var(--ease-out), box-shadow var(--dur-base) var(--ease-out);
}
.sit-dp--sm .sit-dp__trigger { height: var(--control-h-sm); font-size: var(--text-sm); }
.sit-dp__trigger:hover { border-color: var(--border-strong); }
.sit-dp__trigger--open { border-color: var(--border-brand); box-shadow: var(--shadow-focus); }
.sit-dp__trigger:focus-visible { outline: none; border-color: var(--border-brand); box-shadow: var(--shadow-focus); }
.sit-dp__trigger[disabled] { background: var(--surface-sunken); color: var(--text-muted); cursor: not-allowed; }
.sit-dp__cal-ico { color: var(--text-subtle); flex: 0 0 auto; display: inline-flex; }
.sit-dp__cal-ico svg { width: 16px; height: 16px; }
.sit-dp__value { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sit-dp__value--placeholder { color: var(--text-subtle); }
.sit-dp__clear {
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border: none; background: none; color: var(--text-subtle);
  border-radius: var(--radius-pill); cursor: pointer; padding: 0;
}
.sit-dp__clear:hover { background: var(--surface-hover); color: var(--text-body); }
.sit-dp__clear svg { width: 12px; height: 12px; }
.sit-dp--invalid .sit-dp__trigger { border-color: var(--red-500); }
.sit-dp__msg { font-size: var(--text-xs); color: var(--text-muted); }
.sit-dp__msg--error { color: var(--red-600); }

/* ---- Popover ---- */
.sit-dp__pop {
  position: absolute; top: calc(100% + 6px); z-index: var(--z-overlay);
  background: var(--surface-card); border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg); box-shadow: var(--shadow-lg);
  display: flex; animation: sit-dp-in var(--dur-fast) var(--ease-out); overflow: hidden;
}
.sit-dp__pop--left { left: 0; }
.sit-dp__pop--right { right: 0; }
.sit-dp__pop--top { top: auto; bottom: calc(100% + 6px); }
.sit-dp__pop--centered {
  position: fixed !important; top: 50% !important; left: 50% !important;
  right: auto !important; bottom: auto !important;
  transform: translate(-50%, -50%) !important;
  z-index: var(--z-modal, 1060); animation: none;
}
@keyframes sit-dp-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
.sit-dp__presets {
  display: flex; flex-direction: column; gap: 2px; padding: 10px; min-width: 148px;
  border-right: 1px solid var(--border-subtle); background: var(--slate-25);
}
.sit-dp__preset {
  text-align: left; padding: 8px 10px; border: none; background: none; border-radius: var(--radius-sm);
  font-family: var(--font-body); font-size: var(--text-sm); font-weight: var(--weight-medium);
  color: var(--text-body); cursor: pointer; white-space: nowrap;
}
.sit-dp__preset:hover { background: var(--surface-hover); color: var(--text-strong); }
.sit-dp__preset--active { background: var(--surface-brand-subtle); color: var(--blue-700); font-weight: var(--weight-semibold); }

/* ---- Calendar core ---- */
.sit-cal { padding: 14px; user-select: none; }
.sit-cal__panes { display: flex; gap: 22px; }
.sit-cal__pane { width: 244px; }
.sit-cal__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; height: 30px; }
.sit-cal__nav {
  display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px;
  border: 1px solid transparent; background: none; border-radius: var(--radius-md); color: var(--text-body); cursor: pointer;
}
.sit-cal__nav:hover { background: var(--surface-hover); border-color: var(--border-subtle); }
.sit-cal__nav:disabled { opacity: 0.3; cursor: not-allowed; }
.sit-cal__nav:disabled:hover { background: none; border-color: transparent; }
.sit-cal__nav svg { width: 16px; height: 16px; }
.sit-cal__nav--spacer { visibility: hidden; }
.sit-cal__title {
  border: none; background: none; cursor: pointer; padding: 5px 10px; border-radius: var(--radius-md);
  font-family: var(--font-display); font-size: var(--text-md); font-weight: var(--weight-semibold);
  color: var(--text-strong); letter-spacing: var(--tracking-tight);
}
.sit-cal__title:hover { background: var(--surface-hover); }
.sit-cal__grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.sit-cal__wd {
  display: flex; align-items: center; justify-content: center; height: 30px;
  font-size: var(--text-2xs); font-weight: var(--weight-bold); letter-spacing: var(--tracking-caps);
  text-transform: uppercase; color: var(--text-subtle);
}
.sit-cal__cell { position: relative; display: flex; align-items: center; justify-content: center; height: 34px; }
.sit-cal__day {
  position: relative; z-index: 1; display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border: none; background: none; border-radius: var(--radius-md);
  font-family: var(--font-body); font-size: var(--text-sm); font-weight: var(--weight-medium);
  color: var(--text-body); cursor: pointer; transition: background var(--dur-fast), color var(--dur-fast);
}
.sit-cal__day:hover { background: var(--surface-hover); color: var(--text-strong); }
.sit-cal__day--muted { color: var(--text-subtle); opacity: 0.55; }
.sit-cal__day--today { font-weight: var(--weight-bold); color: var(--blue-600); }
.sit-cal__day--today::after {
  content: ''; position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%);
  width: 4px; height: 4px; border-radius: var(--radius-pill); background: var(--blue-500);
}
.sit-cal__day--selected, .sit-cal__day--selected:hover {
  background: var(--action-primary); color: var(--text-on-brand); font-weight: var(--weight-semibold);
}
.sit-cal__day--selected.sit-cal__day--today::after { background: var(--text-on-brand); }
.sit-cal__day:disabled { color: var(--text-subtle); opacity: 0.35; cursor: not-allowed; text-decoration: line-through; }
.sit-cal__day:disabled:hover { background: none; }
/* range fill */
.sit-cal__cell--inrange::before {
  content: ''; position: absolute; z-index: 0; inset: 1px 0; background: var(--blue-50);
}
.sit-cal__cell--range-start::before { left: 50%; border-top-left-radius: var(--radius-md); border-bottom-left-radius: var(--radius-md); }
.sit-cal__cell--range-end::before { right: 50%; border-top-right-radius: var(--radius-md); border-bottom-right-radius: var(--radius-md); }
.sit-cal__cell--range-start.sit-cal__cell--range-end::before { display: none; }
.sit-cal__day--endpoint { background: var(--action-primary); color: var(--text-on-brand); font-weight: var(--weight-semibold); }
.sit-cal__day--endpoint:hover { background: var(--action-primary-hover); color: var(--text-on-brand); }

/* month / year switcher pane */
.sit-cal__switch { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; margin-top: 2px; }
.sit-cal__opt {
  height: 44px; border: 1px solid transparent; background: none; border-radius: var(--radius-md);
  font-family: var(--font-body); font-size: var(--text-sm); font-weight: var(--weight-medium);
  color: var(--text-body); cursor: pointer;
}
.sit-cal__opt:hover { background: var(--surface-hover); }
.sit-cal__opt--active { background: var(--action-primary); color: var(--text-on-brand); font-weight: var(--weight-semibold); }
.sit-cal__opt:disabled { opacity: 0.3; cursor: not-allowed; }
.sit-cal__opt:disabled:hover { background: none; }

/* footer */
.sit-cal__foot {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border-subtle);
}
.sit-cal__foot-range { font-size: var(--text-sm); color: var(--text-body); font-weight: var(--weight-medium); }
.sit-cal__foot-range b { color: var(--text-strong); font-weight: var(--weight-semibold); }
.sit-cal__actions { display: flex; gap: 8px; margin-left: auto; }
.sit-cal__act {
  height: 32px; padding: 0 14px; border-radius: var(--radius-md); cursor: pointer;
  font-family: var(--font-body); font-size: var(--text-sm); font-weight: var(--weight-semibold);
  border: 1px solid var(--border-default); background: var(--surface-card); color: var(--text-body);
}
.sit-cal__act:hover { background: var(--surface-hover); border-color: var(--border-strong); }
.sit-cal__act--primary { background: var(--action-primary); border-color: var(--action-primary); color: var(--text-on-brand); }
.sit-cal__act--primary:hover { background: var(--action-primary-hover); border-color: var(--action-primary-hover); }
.sit-cal__act:disabled { opacity: 0.5; cursor: not-allowed; }

/* ---- Time columns (datetime / time modes) ---- */
.sit-time { display: flex; flex-direction: column; border-left: 1px solid var(--border-subtle); align-self: stretch; }
.sit-time--solo { border-left: none; }
.sit-time__head {
  height: 30px; margin: 14px 14px 10px; display: flex; align-items: center; justify-content: center; gap: 6px;
  font-size: var(--text-2xs); font-weight: var(--weight-bold); letter-spacing: var(--tracking-caps);
  text-transform: uppercase; color: var(--text-subtle);
}
.sit-time__head svg { width: 13px; height: 13px; }
.sit-time__cols { display: flex; flex: 1 1 auto; padding: 0 8px 8px; }
.sit-time__sep { align-self: stretch; display: flex; align-items: center; font-weight: var(--weight-bold); color: var(--text-subtle); padding-top: 2px; }
.sit-time__col {
  position: relative;
  width: 54px; max-height: 238px; overflow-y: auto; padding: 2px; display: flex; flex-direction: column; gap: 2px;
  scrollbar-width: thin; scrollbar-color: var(--slate-300) transparent;
}
.sit-time__col::-webkit-scrollbar { width: 6px; }
.sit-time__col::-webkit-scrollbar-thumb { background: var(--slate-300); border-radius: var(--radius-pill); }
.sit-time__opt {
  flex: 0 0 auto; height: 32px; border: none; background: none; border-radius: var(--radius-md);
  font-family: var(--font-body); font-size: var(--text-sm); font-weight: var(--weight-medium);
  color: var(--text-body); cursor: pointer; font-variant-numeric: tabular-nums;
}
.sit-time__opt:hover { background: var(--surface-hover); color: var(--text-strong); }
.sit-time__opt--active, .sit-time__opt--active:hover {
  background: var(--action-primary, #227ebd);
  color: var(--text-on-brand, #fff);
  box-shadow: inset 0 0 0 1px var(--action-primary, #227ebd);
  font-weight: var(--weight-semibold, 600);
}
.sit-time__ampm { display: flex; flex-direction: column; gap: 4px; padding: 2px 6px 2px 2px; }
`;
  document.head.appendChild(el);
}

/* ---------------- date helpers ---------------- */
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // Monday-first labels

const toDate = v =>
  v == null ? null : v instanceof Date ? new Date(v.getFullYear(), v.getMonth(), v.getDate()) : new Date(v);
const startOfMonth = d => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const sameDay = (a, b) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const isBefore = (a, b) => a.getTime() < b.getTime();
const stripTime = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const clampDay = d => (d ? stripTime(d) : null);

// time-preserving parse + part setters (datetime / time modes)
const toDateTime = v => (v == null ? null : v instanceof Date ? new Date(v.getTime()) : new Date(v));
const withDatePart = (base, day) => {
  const b = base ? new Date(base.getTime()) : new Date(new Date().setSeconds(0, 0));
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), b.getHours(), b.getMinutes(), 0, 0);
};
const withTimePart = (base, h, m) => {
  const b = base ? new Date(base.getTime()) : new Date();
  return new Date(b.getFullYear(), b.getMonth(), b.getDate(), h, m, 0, 0);
};

function formatDate(d, fmt = 'DD MMM YYYY') {
  if (!d) return '';
  const H = d.getHours();
  const h12 = H % 12 === 0 ? 12 : H % 12;
  const map = {
    YYYY: d.getFullYear(),
    MMM: MONTHS_SHORT[d.getMonth()],
    MM: String(d.getMonth() + 1).padStart(2, '0'),
    DD: String(d.getDate()).padStart(2, '0'),
    HH: String(H).padStart(2, '0'),
    hh: String(h12).padStart(2, '0'),
    mm: String(d.getMinutes()).padStart(2, '0'),
    ss: String(d.getSeconds()).padStart(2, '0'),
    A: H < 12 ? 'AM' : 'PM'
  };
  return String(fmt).replace(/YYYY|MMM|MM|DD|HH|hh|mm|ss|A/g, t => map[t]);
}

function buildWeekdays(weekStartsOn) {
  const wd = [];
  for (let i = 0; i < 7; i++) wd.push(WEEKDAYS[(weekStartsOn - 1 + i) % 7]);
  return wd;
}

function monthMatrix(viewDate, weekStartsOn) {
  const first = startOfMonth(viewDate);
  const jsDow = first.getDay(); // 0=Sun
  const offset = (jsDow - weekStartsOn + 7) % 7;
  const gridStart = addDays(first, -offset);
  const cells = [];
  for (let i = 0; i < 42; i++) cells.push(addDays(gridStart, i));
  // Always render six calendar weeks. Keeping the grid height stable prevents
  // range-picker popovers from jumping as users move between months.
  return cells;
}

/* ---------------- Calendar (core grid, always inline) ---------------- */
export function Calendar({
  mode = 'single',
  value,
  onChange,
  month,
  onMonthChange,
  min,
  max,
  weekStartsOn = 1,
  numberOfMonths = 1,
  className = ''
}) {
  const minD = clampDay(toDate(min));
  const maxD = clampDay(toDate(max));
  const isRange = mode === 'range';

  const initView = React.useMemo(() => {
    if (month) return startOfMonth(toDate(month));
    if (isRange && value && value.start) return startOfMonth(toDate(value.start));
    if (!isRange && value) return startOfMonth(toDate(value));
    return startOfMonth(new Date());
  }, []); // eslint-disable-line

  const [view, setView] = React.useState(initView);
  const [pane, setPane] = React.useState('days'); // days | months | years
  const [hover, setHover] = React.useState(null);
  const viewRef = month ? startOfMonth(toDate(month)) : view;

  const setMonth = d => {
    const nv = startOfMonth(d);
    if (!month) setView(nv);
    onMonthChange && onMonthChange(nv);
  };

  const sel = isRange
    ? { start: clampDay(toDate(value && value.start)), end: clampDay(toDate(value && value.end)) }
    : clampDay(toDate(value));

  const disabled = d => (minD && isBefore(d, minD)) || (maxD && isBefore(maxD, d));

  const handleDay = d => {
    if (disabled(d)) return;
    if (!isRange) {
      onChange && onChange(d);
      return;
    }
    const { start, end } = sel;
    if (!start || (start && end)) {
      onChange && onChange({ start: d, end: null });
      return;
    }
    if (isBefore(d, start)) {
      onChange && onChange({ start: d, end: null });
      return;
    }
    onChange && onChange({ start, end: d });
  };

  const rangeBounds = () => {
    if (!isRange) return null;
    const { start, end } = sel;
    if (start && end) return { a: start, b: end };
    if (start && hover && !end) return isBefore(hover, start) ? { a: hover, b: start } : { a: start, b: hover };
    return null;
  };
  const bounds = rangeBounds();

  const cellClass = d => {
    const cls = ['sit-cal__cell'];
    if (bounds && !isBefore(d, bounds.a) && !isBefore(bounds.b, d)) {
      cls.push('sit-cal__cell--inrange');
      if (sameDay(d, bounds.a)) cls.push('sit-cal__cell--range-start');
      if (sameDay(d, bounds.b)) cls.push('sit-cal__cell--range-end');
    }
    return cls.join(' ');
  };
  const dayClass = (d, inMonth) => {
    const cls = ['sit-cal__day'];
    if (!inMonth) cls.push('sit-cal__day--muted');
    if (sameDay(d, new Date())) cls.push('sit-cal__day--today');
    if (!isRange && sameDay(d, sel)) cls.push('sit-cal__day--selected');
    if (isRange && (sameDay(d, sel.start) || sameDay(d, sel.end))) cls.push('sit-cal__day--endpoint');
    return cls.join(' ');
  };

  const wd = buildWeekdays(weekStartsOn);

  const renderMonthGrid = offset => {
    const mv = addMonths(viewRef, offset);
    const cells = monthMatrix(mv, weekStartsOn);
    return (
      <div className="sit-cal__pane" key={offset}>
        <div className="sit-cal__head">
          <button
            type="button"
            className={['sit-cal__nav', offset > 0 ? 'sit-cal__nav--spacer' : ''].join(' ')}
            onClick={() => setMonth(addMonths(viewRef, -1))}
            aria-label="Previous month"
            disabled={minD && isBefore(addMonths(mv, -1), startOfMonth(minD)) && offset === 0}
          >
            <ChevL />
          </button>
          <button type="button" className="sit-cal__title" onClick={() => setPane(offset === 0 ? 'months' : 'days')}>
            {MONTHS[mv.getMonth()]} {mv.getFullYear()}
          </button>
          <button
            type="button"
            className={['sit-cal__nav', offset < numberOfMonths - 1 ? 'sit-cal__nav--spacer' : ''].join(' ')}
            onClick={() => setMonth(addMonths(viewRef, 1))}
            aria-label="Next month"
            disabled={maxD && isBefore(startOfMonth(maxD), addMonths(mv, 1)) && offset === numberOfMonths - 1}
          >
            <ChevR />
          </button>
        </div>
        <div className="sit-cal__grid" role="grid">
          {wd.map(w => (
            <div key={w} className="sit-cal__wd">
              {w[0]}
            </div>
          ))}
          {cells.map((d, i) => {
            const inMonth = d.getMonth() === mv.getMonth();
            return (
              <div key={i} className={cellClass(d)}>
                <button
                  type="button"
                  className={dayClass(d, inMonth)}
                  disabled={disabled(d)}
                  onClick={() => handleDay(d)}
                  onMouseEnter={() => isRange && setHover(d)}
                  aria-label={formatDate(d, 'DD MMM YYYY')}
                >
                  {d.getDate()}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMonthsPane = () => (
    <div className="sit-cal__pane">
      <div className="sit-cal__head">
        <button
          type="button"
          className="sit-cal__nav"
          onClick={() => setMonth(addMonths(viewRef, -12))}
          aria-label="Previous year"
        >
          <ChevL />
        </button>
        <button type="button" className="sit-cal__title" onClick={() => setPane('years')}>
          {viewRef.getFullYear()}
        </button>
        <button
          type="button"
          className="sit-cal__nav"
          onClick={() => setMonth(addMonths(viewRef, 12))}
          aria-label="Next year"
        >
          <ChevR />
        </button>
      </div>
      <div className="sit-cal__switch">
        {MONTHS_SHORT.map((m, i) => (
          <button
            type="button"
            key={m}
            className={['sit-cal__opt', i === viewRef.getMonth() ? 'sit-cal__opt--active' : ''].join(' ')}
            onClick={() => {
              setMonth(new Date(viewRef.getFullYear(), i, 1));
              setPane('days');
            }}
          >
            {m}
          </button>
        ))}
      </div>
    </div>
  );

  const renderYearsPane = () => {
    const base = viewRef.getFullYear() - (viewRef.getFullYear() % 12);
    const years = Array.from({ length: 12 }, (_, i) => base + i);
    return (
      <div className="sit-cal__pane">
        <div className="sit-cal__head">
          <button
            type="button"
            className="sit-cal__nav"
            onClick={() => setMonth(new Date(base - 12, viewRef.getMonth(), 1))}
            aria-label="Previous years"
          >
            <ChevL />
          </button>
          <button type="button" className="sit-cal__title" onClick={() => setPane('days')}>
            {base}–{base + 11}
          </button>
          <button
            type="button"
            className="sit-cal__nav"
            onClick={() => setMonth(new Date(base + 12, viewRef.getMonth(), 1))}
            aria-label="Next years"
          >
            <ChevR />
          </button>
        </div>
        <div className="sit-cal__switch">
          {years.map(y => (
            <button
              type="button"
              key={y}
              className={['sit-cal__opt', y === viewRef.getFullYear() ? 'sit-cal__opt--active' : ''].join(' ')}
              disabled={(minD && y < minD.getFullYear()) || (maxD && y > maxD.getFullYear())}
              onClick={() => {
                setMonth(new Date(y, viewRef.getMonth(), 1));
                setPane('months');
              }}
            >
              {y}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={['sit-cal', className].filter(Boolean).join(' ')} onMouseLeave={() => setHover(null)}>
      <div className="sit-cal__panes">
        {pane === 'months'
          ? renderMonthsPane()
          : pane === 'years'
          ? renderYearsPane()
          : Array.from({ length: numberOfMonths }, (_, i) => renderMonthGrid(i))}
      </div>
    </div>
  );
}
Calendar.displayName = 'Calendar';

/* ---------------- built-in range presets ---------------- */
export function rangePresets() {
  const today = stripTime(new Date());
  const mk = (start, end) => ({ start, end });
  return [
    { key: 'today', label: 'Today', range: () => mk(today, today) },
    { key: 'yesterday', label: 'Yesterday', range: () => mk(addDays(today, -1), addDays(today, -1)) },
    { key: 'last7', label: 'Last 7 days', range: () => mk(addDays(today, -6), today) },
    { key: 'last30', label: 'Last 30 days', range: () => mk(addDays(today, -29), today) },
    {
      key: 'thismonth',
      label: 'This month',
      range: () => mk(startOfMonth(today), new Date(today.getFullYear(), today.getMonth() + 1, 0))
    },
    {
      key: 'lastmonth',
      label: 'Last month',
      range: () => mk(startOfMonth(addMonths(today, -1)), new Date(today.getFullYear(), today.getMonth(), 0))
    },
    {
      key: 'thisyear',
      label: 'This year',
      range: () => mk(new Date(today.getFullYear(), 0, 1), new Date(today.getFullYear(), 11, 31))
    }
  ];
}

/* ---------------- TimeColumns (hour / minute scroll lists) ---------------- */
function TimeColumns({ value, onChange, minuteStep = 5, use12h = false, solo = false }) {
  const base = value || new Date(new Date().setSeconds(0, 0));
  const H = base.getHours();
  const M = base.getMinutes();
  const hourRef = React.useRef(null);
  const minRef = React.useRef(null);

  const hours = use12h ? Array.from({ length: 12 }, (_, i) => i + 1) : Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep);
  const activeHour = use12h ? (H % 12 === 0 ? 12 : H % 12) : H;
  const isPM = H >= 12;

  React.useLayoutEffect(() => {
    const scroll = () => {
      [hourRef, minRef].forEach(r => {
        const el = r.current && r.current.querySelector('.sit-time__opt--active');
        if (el && r.current) r.current.scrollTop = el.offsetTop - r.current.clientHeight / 2 + el.clientHeight / 2;
      });
    };
    scroll();
    const id = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(id);
  }, []); // eslint-disable-line

  const to24 = (h12, pm) => (h12 % 12) + (pm ? 12 : 0);
  const setHour = h => onChange(withTimePart(base, use12h ? to24(h, isPM) : h, M));
  const setMin = m => onChange(withTimePart(base, H, m));
  const setAmPm = pm => onChange(withTimePart(base, use12h ? to24(activeHour, pm) : H, M));

  return (
    <div className={['sit-time', solo ? 'sit-time--solo' : ''].filter(Boolean).join(' ')}>
      <div className="sit-time__head">
        <ClockIcon /> Time
      </div>
      <div className="sit-time__cols">
        <div className="sit-time__col" ref={hourRef}>
          {hours.map(h => (
            <button
              type="button"
              key={h}
              className={['sit-time__opt', h === activeHour ? 'sit-time__opt--active' : ''].join(' ')}
              onClick={() => setHour(h)}
            >
              {String(h).padStart(2, '0')}
            </button>
          ))}
        </div>
        <span className="sit-time__sep">:</span>
        <div className="sit-time__col" ref={minRef}>
          {minutes.map(m => (
            <button
              type="button"
              key={m}
              className={['sit-time__opt', m === M ? 'sit-time__opt--active' : ''].join(' ')}
              onClick={() => setMin(m)}
            >
              {String(m).padStart(2, '0')}
            </button>
          ))}
        </div>
        {use12h ? (
          <div className="sit-time__ampm">
            <button
              type="button"
              className={['sit-time__opt', !isPM ? 'sit-time__opt--active' : ''].join(' ')}
              onClick={() => setAmPm(false)}
            >
              AM
            </button>
            <button
              type="button"
              className={['sit-time__opt', isPM ? 'sit-time__opt--active' : ''].join(' ')}
              onClick={() => setAmPm(true)}
            >
              PM
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ---------------- DatePicker (field + popover) ---------------- */
export function DatePicker({
  label,
  mode = 'single',
  value,
  onChange,
  placeholder,
  format,
  min,
  max,
  weekStartsOn = 1,
  numberOfMonths,
  presets,
  align = 'left',
  placement = 'bottom',
  centered = false,
  size = 'md',
  minuteStep = 5,
  use12h = false,
  required = false,
  disabled = false,
  clearable = true,
  syncRangeOnSelect = false,
  hint,
  error,
  id,
  className = ''
}) {
  const isRange = mode === 'range';
  const isDateTime = mode === 'datetime';
  const isTime = mode === 'time';
  const buffered = isRange || isDateTime || isTime; // require Apply
  const fmt = format || (isDateTime ? 'DD MMM YYYY · HH:mm' : isTime ? 'HH:mm' : 'DD MMM YYYY');

  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(value); // editing buffer for buffered modes
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (open) setDraft(value);
  }, [open]); // eslint-disable-line
  React.useEffect(() => {
    if (!open) return;
    const h = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const esc = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const presetList = presets === true ? rangePresets() : Array.isArray(presets) ? presets : null;
  const months = numberOfMonths || (isRange ? 2 : 1);

  const display = () => {
    if (isRange) {
      const s = value && value.start ? formatDate(toDate(value.start), fmt) : '';
      const e = value && value.end ? formatDate(toDate(value.end), fmt) : '';
      if (!s && !e) return null;
      return `${s || '…'} — ${e || '…'}`;
    }
    return value ? formatDate(toDateTime(value), fmt) : null;
  };
  const shown = display();
  const emptyVal = isRange ? { start: null, end: null } : null;

  const commitSingle = v => {
    onChange && onChange(v);
    setOpen(false);
  };
  const applyBuffer = () => {
    onChange && onChange(draft);
    setOpen(false);
  };
  const changeRange = nextRange => {
    setDraft(nextRange);
    if (syncRangeOnSelect) {
      onChange && onChange(nextRange);
      if (nextRange && nextRange.start && nextRange.end) {
        setOpen(false);
      }
    }
  };
  const selectPreset = preset => {
    const nextRange = preset.range();
    setDraft(nextRange);
    if (syncRangeOnSelect) {
      onChange && onChange(nextRange);
      setOpen(false);
    }
  };
  const activePreset = () => {
    if (!presetList || !draft || !draft.start || !draft.end) return null;
    for (const p of presetList) {
      const r = p.range();
      if (sameDay(toDate(r.start), toDate(draft.start)) && sameDay(toDate(r.end), toDate(draft.end))) return p.key;
    }
    return null;
  };

  const applyDisabled = isRange ? !draft || !draft.start || !draft.end : !draft;
  const placeholderText =
    placeholder ||
    (isRange ? 'Select date range' : isTime ? 'Select time' : isDateTime ? 'Select date & time' : 'Select date');
  const inputId = id || (label ? `dp-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);

  return (
    <div
      className={['sit-dp', size === 'sm' ? 'sit-dp--sm' : '', error ? 'sit-dp--invalid' : '', className]
        .filter(Boolean)
        .join(' ')}
      ref={ref}
    >
      {label ? (
        <label className="sit-dp__label" htmlFor={inputId}>
          {label}
          {required ? <span className="sit-dp__req">*</span> : null}
        </label>
      ) : null}
      <button
        type="button"
        id={inputId}
        disabled={disabled}
        className={['sit-dp__trigger', open ? 'sit-dp__trigger--open' : ''].filter(Boolean).join(' ')}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <span className="sit-dp__cal-ico">{isTime ? <ClockIcon /> : <CalIcon />}</span>
        <span className={['sit-dp__value', shown ? '' : 'sit-dp__value--placeholder'].filter(Boolean).join(' ')}>
          {shown || placeholderText}
        </span>
        {clearable && shown && !disabled ? (
          <span
            className="sit-dp__clear"
            role="button"
            aria-label="Clear"
            onClick={e => {
              e.stopPropagation();
              onChange && onChange(emptyVal);
            }}
          >
            <XIcon />
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className={[
            'sit-dp__pop',
            `sit-dp__pop--${align}`,
            `sit-dp__pop--${placement}`,
            centered ? 'sit-dp__pop--centered' : ''
          ]
            .filter(Boolean)
            .join(' ')}
          role="dialog"
        >
          {isRange && presetList ? (
            <div className="sit-dp__presets">
              {presetList.map(p => (
                <button
                  type="button"
                  key={p.key}
                  className={['sit-dp__preset', activePreset() === p.key ? 'sit-dp__preset--active' : ''].join(' ')}
                  onClick={() => selectPreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          ) : null}
          <div>
            <div style={{ display: 'flex' }}>
              {!isTime ? (
                <Calendar
                  mode={isDateTime ? 'single' : mode}
                  value={buffered ? draft : value}
                  onChange={
                    isDateTime ? d => setDraft(prev => withDatePart(prev, d)) : isRange ? changeRange : commitSingle
                  }
                  min={min}
                  max={max}
                  weekStartsOn={weekStartsOn}
                  numberOfMonths={months}
                />
              ) : null}
              {isDateTime || isTime ? (
                <TimeColumns value={draft} onChange={setDraft} minuteStep={minuteStep} use12h={use12h} solo={isTime} />
              ) : null}
            </div>
            {buffered && !syncRangeOnSelect ? (
              <div style={{ padding: '0 14px 14px' }}>
                <div className="sit-cal__foot">
                  <span className="sit-cal__foot-range">
                    {isRange ? (
                      <>
                        {draft && draft.start ? <b>{formatDate(toDate(draft.start), fmt)}</b> : '—'}
                        {' → '}
                        {draft && draft.end ? <b>{formatDate(toDate(draft.end), fmt)}</b> : '—'}
                      </>
                    ) : draft ? (
                      <b>{formatDate(toDateTime(draft), fmt)}</b>
                    ) : (
                      '—'
                    )}
                  </span>
                  <div className="sit-cal__actions">
                    <button type="button" className="sit-cal__act" onClick={() => setOpen(false)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="sit-cal__act sit-cal__act--primary"
                      disabled={applyDisabled}
                      onClick={applyBuffer}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {error ? (
        <span className="sit-dp__msg sit-dp__msg--error">{error}</span>
      ) : hint ? (
        <span className="sit-dp__msg">{hint}</span>
      ) : null}
    </div>
  );
}
DatePicker.displayName = 'DatePicker';

/* ---------------- icons ---------------- */
function CalIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
      <path d="M3 9h18M8 2.5v4M16 2.5v4" />
    </svg>
  );
}
function XIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M5 5l14 14M19 5L5 19" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </svg>
  );
}
function ChevL() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 6l-6 6 6 6" />
    </svg>
  );
}
function ChevR() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export { formatDate };
